import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { PollAPI } from '../api/PollAPI.js';

const initState = (hideClicked: boolean = false) => {
  const user = useObservable(() => currentUser<Student | Mitarbeiter>(), []);
  const polls = useObservable(
    () =>
      PollAPI.subscribeRelevantPolls(hideClicked)({
        _id: 1,
        public: 1,
        url: 1,
        name: 1,
        showClass: 1,
        products: 1,
        roles: 1,
        studentType: 1,
        mapping: {
          pollKey: 1,
          entityField: 1,
        },
      }),
    [],
  );

  return { user, polls };
};

export const CWPollNotification = stateful(() => {
  const { polls } = initState();

  if (polls?.length === 0) {
    return html`<cr-text>Aktuell gibt es keine Umfragen.</cr-flex>`;
  }

  return html`<cr-flex space="md">
    <cr-text>
      Hier kannst du nochmal alle aktuell offenen Umfragen sehen, auch wenn du diese schon beantwortet hast.
    </cr-text>
    ${repeat(
      polls ?? [],
      p => p._id,
      p => {
        return html`<cr-flex horizontal space-between>
          <cr-flex space="md">
            <cr-text bold>Umfrage</cr-text>
            <cr-text ${css({ fontSize: '1.2rem' })}>${p.name}</cr-text>
          </cr-flex>
          <cr-button
            @click=${async () => {
              ChromiumDialog.guard(async () => {
                const url = await PollAPI.generateURL(p._id)();
                window.open(url, '_blank');
                await PollAPI.registerClick(p._id)();
              });
            }}
            >Teilnehmen!</cr-button
          >
        </cr-flex>`;
      },
    )}
  </cr-flex>`;
});

export const CWPollNotificationGuard = stateful((props: { normal: () => Renderable }) => {
  const { polls } = initState(true);

  if ((polls?.length ?? 0) == 0) {
    return props.normal();
  }

  return html`<cr-flex space="xl" padding="xxl">
    <cr-card space="xl" padding="xl">
      <cr-flex space="md" horizontal crossaxis-center>
        <cr-icon ${css({ fontSize: '2.0rem' })}>warning</cr-icon>
        <cr-flex space="md">
          <cr-h1>Offene Umfragen</cr-h1>
          <cr-text>Um den CHANCENcampus zu benutzen fülle bitte die aktuellen Umfragen aus.</cr-text>
        </cr-flex>
      </cr-flex>
      <cr-hr></cr-hr>
      ${repeat(
        polls ?? [],
        p => p._id,
        p => {
          return html`<cr-flex horizontal space-between>
            <cr-flex space="md">
              <cr-text bold>Umfrage</cr-text>
              <cr-text ${css({ fontSize: '1.2rem' })}>${p.name}</cr-text>
            </cr-flex>
            <cr-button
              @click=${async () => {
                ChromiumDialog.guard(async () => {
                  const url = await PollAPI.generateURL(p._id)();
                  window.open(url, '_blank');
                  await PollAPI.registerClick(p._id)();
                });
              }}
              >Teilnehmen!</cr-button
            >
          </cr-flex>`;
        },
      )}
    </cr-card>
  </cr-flex>`;
});
